import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import {
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { ApiGet, ApiPost, ApiPut } from '../ApiSetup/Api';
import { toast } from 'react-toastify';

const Communication = () => {
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [error, setError] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testOtp, setTestOtp] = useState('');
  const [sending, setSending] = useState(false);
  
  // View template dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [viewTab, setViewTab] = useState(0);
  
  // Edit template dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editChannel, setEditChannel] = useState('sms');
  const [editContent, setEditContent] = useState('');
  const [editTemplateId, setEditTemplateId] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPreferences = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiGet('admin/communication/preferences');
      if (response?.data?.success) {
        setPreferences(response.data.data.templates);
        setGlobalSettings(response.data.data.globalSettings);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load preferences');
      console.error('Error fetching preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, []);

  const handleToggle = async (templateKey, channel, currentValue, mandatory) => {
    // For mandatory templates, ensure at least one channel stays enabled
    if (mandatory && currentValue) {
      const template = preferences.find(t => t.key === templateKey);
      const otherChannel = channel === 'sms' ? 'whatsapp' : 'sms';
      if (!template[otherChannel]?.enabled) {
        toast.warning('At least one channel must be enabled for mandatory templates');
        return;
      }
    }

    try {
      const response = await ApiPost('admin/communication/toggle', {
        templateKey,
        channel,
        enabled: !currentValue
      });

      if (response?.data?.success) {
        toast.success(response.data.message);
        setPreferences(prev =>
          prev.map(t =>
            t.key === templateKey
              ? { ...t, [channel]: { ...t[channel], enabled: !currentValue } }
              : t
          )
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to toggle channel');
    }
  };

  const handleGlobalToggle = async (setting, currentValue) => {
    try {
      const response = await ApiPut('admin/communication/global-settings', {
        [setting]: !currentValue
      });

      if (response?.data?.success) {
        toast.success('Global setting updated');
        setGlobalSettings(prev => ({ ...prev, [setting]: !currentValue }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update setting');
    }
  };

  const handleTestOtp = async () => {
    if (!testPhone || !testOtp) {
      toast.error('Please enter phone number and OTP');
      return;
    }

    setSending(true);
    try {
      const response = await ApiPost('admin/communication/test-otp', {
        phoneNumber: testPhone,
        otp: testOtp
      });

      if (response?.data?.success) {
        toast.success('OTP sent successfully via WhatsApp!');
        setTestDialogOpen(false);
        setTestPhone('');
        setTestOtp('');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSending(false);
    }
  };

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setViewTab(0);
    setViewDialogOpen(true);
  };

  const handleEditTemplate = (template, channel) => {
    setSelectedTemplate(template);
    setEditChannel(channel);
    setEditContent(template[channel]?.content || '');
    setEditTemplateId(channel === 'sms' ? template.sms?.templateId : template.whatsapp?.templateName);
    setEditDialogOpen(true);
  };

  const handleSaveContent = async () => {
    if (!selectedTemplate) return;

    setSaving(true);
    try {
      const payload = {
        channel: editChannel,
        content: editContent
      };
      
      if (editChannel === 'sms') {
        payload.templateId = editTemplateId;
      } else {
        payload.templateName = editTemplateId;
      }

      const response = await ApiPut(`admin/communication/template/${selectedTemplate.key}/content`, payload);

      if (response?.data?.success) {
        toast.success('Template content updated');
        setEditDialogOpen(false);
        fetchPreferences();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update content');
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />;
      case 'rejected':
        return <WarningIcon sx={{ color: 'error.main', fontSize: 16 }} />;
      default:
        return <ScheduleIcon sx={{ color: 'warning.main', fontSize: 16 }} />;
    }
  };

  const getStatusChip = (status) => {
    const config = {
      approved: { label: 'Approved', color: 'success' },
      rejected: { label: 'Rejected', color: 'error' },
      pending: { label: 'Pending', color: 'warning' }
    };
    const { label, color } = config[status] || config.pending;
    return <Chip label={label} color={color} size="small" sx={{ borderRadius: '8px' }} />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchPreferences}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px', mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Communication Preferences
            </Typography>
            <Typography color="text.secondary" mt={0.5}>
              Manage SMS and WhatsApp channels for different message templates
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Test WhatsApp OTP">
              <Button
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                onClick={() => setTestDialogOpen(true)}
                sx={{ borderRadius: '12px' }}
              >
                Test OTP
              </Button>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchPreferences}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Global Settings */}
      <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px', mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Global Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ borderRadius: '16px' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <SmsIcon color="primary" />
                    <Typography fontWeight={500}>SMS Enabled</Typography>
                  </Box>
                  <Switch
                    checked={globalSettings?.smsEnabled ?? false}
                    onChange={() => handleGlobalToggle('smsEnabled', globalSettings?.smsEnabled)}
                    color="primary"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Master switch for all SMS communications
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ borderRadius: '16px' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <WhatsAppIcon sx={{ color: '#25D366' }} />
                    <Typography fontWeight={500}>WhatsApp Enabled</Typography>
                  </Box>
                  <Switch
                    checked={globalSettings?.whatsappEnabled ?? false}
                    onChange={() => handleGlobalToggle('whatsappEnabled', globalSettings?.whatsappEnabled)}
                    color="success"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Master switch for all WhatsApp communications
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ borderRadius: '16px' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <SmsIcon color="warning" />
                    <Typography fontWeight={500}>Fallback to SMS</Typography>
                  </Box>
                  <Switch
                    checked={globalSettings?.fallbackToSms ?? false}
                    onChange={() => handleGlobalToggle('fallbackToSms', globalSettings?.fallbackToSms)}
                    color="warning"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Send SMS if WhatsApp fails
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Templates Table */}
      <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px' }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Message Templates
        </Typography>
        <Typography color="text.secondary" mb={3}>
          Toggle SMS/WhatsApp for each template. Click the eye icon to view template content.
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Template</strong></TableCell>
                <TableCell><strong>Trigger Point</strong></TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <SmsIcon fontSize="small" color="primary" />
                    <strong>SMS</strong>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                    <strong>WhatsApp</strong>
                  </Box>
                </TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preferences?.map((template) => (
                <TableRow key={template.key} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {template.mandatory && (
                        <Tooltip title="Mandatory - At least one channel required">
                          <LockIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                      <Box>
                        <Typography fontWeight={500}>{template.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {template.nameGuj}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                      {template.triggerPoint || template.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                      <Switch
                        checked={template.sms?.enabled ?? false}
                        onChange={() => handleToggle(template.key, 'sms', template.sms?.enabled, template.mandatory)}
                        color="primary"
                        disabled={!globalSettings?.smsEnabled}
                        size="small"
                      />
                      {getStatusChip(template.sms?.status)}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                      <Switch
                        checked={template.whatsapp?.enabled ?? false}
                        onChange={() => handleToggle(template.key, 'whatsapp', template.whatsapp?.enabled, template.mandatory)}
                        color="success"
                        disabled={!globalSettings?.whatsappEnabled}
                        size="small"
                      />
                      {getStatusChip(template.whatsapp?.status)}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title="View Template">
                        <IconButton size="small" onClick={() => handleViewTemplate(template)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit SMS">
                        <IconButton size="small" onClick={() => handleEditTemplate(template, 'sms')}>
                          <SmsIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit WhatsApp">
                        <IconButton size="small" onClick={() => handleEditTemplate(template, 'whatsapp')}>
                          <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View Template Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <VisibilityIcon />
            {selectedTemplate?.name}
            {selectedTemplate?.mandatory && (
              <Chip label="Mandatory" color="warning" size="small" sx={{ ml: 1 }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <>
              <Typography color="text.secondary" mb={2}>
                <strong>Trigger:</strong> {selectedTemplate.triggerPoint}
              </Typography>
              
              <Tabs value={viewTab} onChange={(e, v) => setViewTab(v)} sx={{ mb: 2 }}>
                <Tab icon={<SmsIcon />} label="SMS Template" />
                <Tab icon={<WhatsAppIcon />} label="WhatsApp Template" />
              </Tabs>

              {viewTab === 0 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2">SMS Template ID: {selectedTemplate.sms?.templateId || 'Not set'}</Typography>
                    {getStatusChip(selectedTemplate.sms?.status)}
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: '12px' }}>
                    <Typography sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {selectedTemplate.sms?.content || 'No content defined'}
                    </Typography>
                  </Paper>
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Variables:</strong> {selectedTemplate.sms?.variables?.join(', ') || 'None'}
                    </Typography>
                  </Box>
                </Box>
              )}

              {viewTab === 1 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2">Template Name: {selectedTemplate.whatsapp?.templateName || 'Not set'}</Typography>
                    {getStatusChip(selectedTemplate.whatsapp?.status)}
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#dcf8c6', borderRadius: '12px' }}>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedTemplate.whatsapp?.content || 'No content defined'}
                    </Typography>
                  </Paper>
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Variables:</strong> {selectedTemplate.whatsapp?.variables?.join(', ') || 'None'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon />
            Edit {editChannel === 'sms' ? 'SMS' : 'WhatsApp'} Template - {selectedTemplate?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={editChannel === 'sms' ? 'Template ID' : 'Template Name'}
            value={editTemplateId}
            onChange={(e) => setEditTemplateId(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            helperText={editChannel === 'sms' ? 'DLT registered template ID' : 'WhatsApp Business approved template name'}
          />
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Message Content"
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            helperText={`Variables: ${selectedTemplate?.[editChannel]?.variables?.join(', ') || 'None'}`}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> For SMS, ensure the template is DLT approved. For WhatsApp, the template must be approved in WhatsApp Business Manager.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveContent}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test OTP Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WhatsAppIcon sx={{ color: '#25D366' }} />
            Test WhatsApp OTP
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" mb={2}>
            Send a test OTP via WhatsApp to verify the integration is working.
          </Typography>
          <TextField
            fullWidth
            label="Phone Number"
            placeholder="9876543210"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="OTP Code"
            placeholder="123456"
            value={testOtp}
            onChange={(e) => setTestOtp(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleTestOtp}
            disabled={sending}
            sx={{ borderRadius: '12px' }}
          >
            Send Test OTP
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Communication;
